import React, { useCallback, useEffect, useRef, useState } from "react";
import { getSameVariants } from "utils/api";
import { useDispatch, useSelector } from "react-redux";
import { getMerchantConfig, getRmsConfig } from "features/config/configSlice";
import {
  addNewProduct,
  addProceedBool,
  addUploads,
  getSelectedGroupProducts,
  getSelectedProduct,
  getSelectedReason,
  getSelectedSubReason,
  getUploadMediaIds,
  mobSelected,
  returnQuantity,
  returnType,
  getReplacementMode,
  setReplacementMode,
} from "features/product/productSlice";
import {
  addReturnedProducts,
  getReturnedProducts,
} from "features/returnedProducts/returnedProductSlice";
import { Button, Modal } from "@mui/material";
import {
  getSelectedOrder,
  updateWebSelected,
} from "features/orders/orderSlice";
// import { ReactComponent as FrownIcon } from "assets/frown.svg";
import { StyledDifferentProduct } from "./styled";
import { ReactComponent as Search } from "assets/search.svg";
import SizeSelector from "./SizeSelector";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getLineItemMetadata } from "utils/lineItemMetadata";
import parse from "html-react-parser";
import LoadingError from "components/v2/Common/LoadingError";
import { ReactComponent as CloseIcon } from "assets/close.svg";
import { isLineItemReplacementEligible } from "utils/replacementEligibility";
import { getEffectiveResolutionConfig } from "utils/reasonResolution";

const sameVariantCache = new Map();
const sameVariantInflightCache = new Map();

function SameProduct({
  quantityInc,
  productType,
  selectedVariant,
  setSelectedVariant,
  otherReason,
  setAnimate,
  setUploads,
  onExchangeComplete,
  onBack,
  actionLabel = "Select for exchange",
  registerSameSubmit,
  showInternalContinue = true,
}) {
  const [sameVariantProducts, setSameVariantProducts] = useState({});
  const [sameVariantLoader, setSameVariantLoader] = useState(true);
  const [sameApiError, setSameApiError] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState({});
  const [localSelectedVariant, setLocalSelectedVariant] = useState(null);
  const [customProperties, setCustomProperties] = useState([]);
  const [customSizeError, setCustomSizeError] = useState(false);
  const [customProductGroupId, setCustomProductGroupId] = useState("");
  const [sameVariantConfirmationOpen, setSameVariantConfirmationOpen] =
    useState(false);
  const hasAutoProcessedRef = useRef(false);

  const merchantConfig = useSelector(getMerchantConfig);
  const rmsConfig = useSelector(getRmsConfig);
  const selectedProduct = useSelector(getSelectedProduct);
  const returnedProducts = useSelector(getReturnedProducts);
  const reason = useSelector(getSelectedReason);
  const subReason = useSelector(getSelectedSubReason);
  const selectedOrder = useSelector(getSelectedOrder);
  const selectedGroupProducts = useSelector(getSelectedGroupProducts);
  const isReplacement = useSelector(getReplacementMode);
  const uploadMediaIds = useSelector(getUploadMediaIds);

  const dispatch = useDispatch();
  const effectiveSelectedVariant =
    localSelectedVariant !== null ? localSelectedVariant : selectedVariant;

  const isOriginalVariant = useCallback(
    (variant) =>
      variant &&
      selectedProduct &&
      String(variant.variant_id) === String(selectedProduct.variant_id),
    [selectedProduct],
  );

  const isSameVariantSelected = isOriginalVariant(effectiveSelectedVariant);

  const findOriginalVariant = useCallback(
    (variants = []) =>
      variants.find(
        (variant) =>
          String(variant.variant_id) === String(selectedProduct?.variant_id),
      ) || null,
    [selectedProduct?.variant_id],
  );

  const openSameVariantConfirmationIfNeeded = useCallback((variant) => {
    if (isOriginalVariant(variant)) {
      setSameVariantConfirmationOpen(true);
    }
  }, [isOriginalVariant]);

  const orderRequiredProduct = (product) => {
    const unorderedProduct = { ...product };

    // Sort the attributes
    unorderedProduct.attributes = unorderedProduct.attributes.sort((a, b) => {
      return a.position - b.position;
    });

    // Sort the 'values' array inside each attribute and remove values that
    // do not exist in any variant. If an attribute itself has no matching
    // variants, drop the attribute entirely.

    // Map of attribute id to the set of value ids present in variants
    const availableValuesMap = {};
    unorderedProduct.variants.forEach((variant) => {
      variant.attributes.forEach((va) => {
        if (!availableValuesMap[va.product_attribute]) {
          availableValuesMap[va.product_attribute] = new Set();
        }
        availableValuesMap[va.product_attribute].add(va.value);
      });
    });

    unorderedProduct.attributes = unorderedProduct.attributes
      .map((attribute) => {
        if (!availableValuesMap[attribute.id]) {
          return null; // attribute not present in any variant
        }

        const validValues = (attribute.values || [])
          .filter((val) => availableValuesMap[attribute.id].has(val.id))
          .sort((a, b) => a.order - b.order);

        return { ...attribute, values: validValues };
      })
      .filter(Boolean);

    const findSelectedVariant = (variants, attributes) => {
      return variants.find((variant) => {
        return attributes.every((attribute) => {
          const matchingVariantAttribute = variant.attributes.find(
            (variantAttribute) =>
              variantAttribute.product_attribute === attribute.id &&
              attribute.values.some(
                (value) => value.id === variantAttribute.value,
              ),
          );
          return Boolean(matchingVariantAttribute);
        });
      });
    };

    let defaultSelectedVariant = isReplacement
      ? findOriginalVariant(unorderedProduct.variants)
      : null;

    if (!defaultSelectedVariant && !isReplacement) {
      defaultSelectedVariant = findSelectedVariant(
        unorderedProduct.variants,
        unorderedProduct.attributes,
      );
    }

    if (!defaultSelectedVariant && !isReplacement) {
      defaultSelectedVariant = unorderedProduct.variants[0];
    }

    if (!defaultSelectedVariant) {
      setSameVariantProducts(unorderedProduct);
      setSelectedCategories({});
      setLocalSelectedVariant(null);
      return;
    }

    const initialSelectedValues = {};
    unorderedProduct.attributes.forEach((category) => {
      const variantAttr = defaultSelectedVariant.attributes.find(
        (attr) => attr.product_attribute === category.id,
      );
      if (variantAttr) {
        initialSelectedValues[category.id] = variantAttr.value;
      } else {
        initialSelectedValues[category.id] = category?.values?.[0]?.id;
      }
    });

    setSameVariantProducts(unorderedProduct);
    setSelectedCategories(initialSelectedValues);
    setLocalSelectedVariant(
      defaultSelectedVariant || unorderedProduct.variants?.[0] || null,
    );
  };

  const isVariantUnavailableForReplacement = (variant) =>
    Boolean(
      variant &&
        (variant.out_of_stock ||
          !variant.quantity_available_for_exchange ||
          (merchantConfig.merchant_id === 541 &&
            variant.inventory_quantity < 7)),
    );

  useEffect(() => {
    let cancelled = false;
    const cacheKey = [
      merchantConfig.merchant_id,
      selectedProduct.product_id,
      quantityInc,
      selectedProduct.product_price,
      selectedProduct.variant_id,
      selectedProduct.sku,
      selectedOrder.exchange_configurations?.join(",") || "",
      rmsConfig.product_service_version || "",
    ].join("|");

    const cached = sameVariantCache.get(cacheKey);
    if (cached) {
      orderRequiredProduct(cached.products);
      setSameVariantLoader(false);
      return () => {
        cancelled = true;
      };
    }

    const applyVariantPayload = (payload) => {
      if (cancelled) {
        return;
      }
      orderRequiredProduct(payload.products);
      setSameVariantLoader(false);
    };

    const handleVariantError = (err) => {
      if (cancelled) {
        return;
      }
      console.log(err);
      setSameApiError(true);
    };

    const existingRequest = sameVariantInflightCache.get(cacheKey);
    if (existingRequest) {
      existingRequest.then(applyVariantPayload).catch(handleVariantError);
      return () => {
        cancelled = true;
      };
    }

    const request = getSameVariants(
      merchantConfig.merchant_id,
      selectedProduct.product_id,
      quantityInc,
      selectedProduct.product_price,
      selectedProduct.variant_id,
      selectedProduct.sku,
      selectedOrder.exchange_configurations.length > 0
        ? selectedOrder.exchange_configurations.includes("lower_value_product")
        : undefined,
      selectedOrder.exchange_configurations.length > 0
        ? selectedOrder.exchange_configurations.includes("higher_value_product")
        : undefined,
      rmsConfig.product_service_version,
    )
      .then((resp) => {
        const payload = {
          api_version: resp.data.api_version,
          products: resp.data.products,
        };
        sameVariantCache.set(cacheKey, payload);
        return payload;
      })
      .finally(() => {
        sameVariantInflightCache.delete(cacheKey);
      });

    sameVariantInflightCache.set(cacheKey, request);
    request.then(applyVariantPayload).catch(handleVariantError);

    return () => {
      cancelled = true;
    };
  }, []);

  const handleProductExchange = () => {
    if (customSizeError) return;
    const requestReturnType = isReplacement ? "replacement" : "exchange";
    const resolutionConfig = reason?.reason_mode === "reason_first"
      ? getEffectiveResolutionConfig(reason, subReason, requestReturnType)
      : {};
    const shipmentApplicable = resolutionConfig.shipment_applicable ?? (
      isReplacement
        ? rmsConfig.shipment_applicable_for_replacements !== false
        : selectedProduct.shipment_applicable
    );

    dispatch(mobSelected(false));
    dispatch(updateWebSelected(false));
    if (setSelectedVariant) {
      setSelectedVariant(effectiveSelectedVariant);
    }
    dispatch(addNewProduct(effectiveSelectedVariant));
    dispatch(returnQuantity(quantityInc));
    dispatch(returnType(requestReturnType));
    dispatch(addUploads([]));
    setUploads([]);
    let tempProducts = [...returnedProducts];
    let line_item_ids = tempProducts.map((a) => String(a.line_item_id));

    const exchangeVariant = effectiveSelectedVariant;

    if (!exchangeVariant) {
      return;
    }

    const exchangeProductTitle =
      !isReplacement && sameVariantProducts?.title
        ? sameVariantProducts.title
        : selectedProduct.product_title;

    const exchangeProductPrice = isReplacement
      ? selectedProduct.product_price
      : exchangeVariant.price;

    const exchangeOriginalPrice = isReplacement
      ? selectedProduct.original_product_price
      : exchangeVariant.original_product_price;

    const exchangeVariantImage =
      (exchangeVariant.variant_img && exchangeVariant.variant_img.src) ||
      exchangeVariant.variant_img ||
      selectedProduct.image_url;

    const exchangeVariantName =
      exchangeVariant.title || selectedProduct.variant_name;

    if (selectedGroupProducts.length > 0) {
      for (let i = selectedGroupProducts.length - 1; i >= 0; i--) {
        const product = selectedGroupProducts[i];

        const index = line_item_ids.indexOf(String(product.line_item_id));

        if (index !== -1) {
          tempProducts.splice(index, 1);
          line_item_ids.splice(index, 1);
        }
      }
    } else {
      if (line_item_ids.indexOf(String(selectedProduct.line_item_id)) !== -1) {
        let index = line_item_ids.indexOf(String(selectedProduct.line_item_id));
        tempProducts.splice(index, 1);
      }
    }

    let childProducts = selectedGroupProducts.filter(
      (groupProduct) =>
        groupProduct.is_child === true &&
        (isReplacement
          ? rmsConfig.supports_replacement &&
            isLineItemReplacementEligible(groupProduct)
          : groupProduct.exchange_eligible === true) &&
        groupProduct.auto_add === true,
    );
    const selectedLineMedia = uploadMediaIds.find(
      (item) =>
        String(item.line_item_id) === String(selectedProduct.line_item_id),
    );

    const nextReturnedProducts = [
      ...tempProducts,
      {
        merchant_id: merchantConfig.merchant_id,
        line_item_id: selectedProduct.line_item_id,

        product_id: selectedProduct.product_id,
        variant_id: selectedProduct.variant_id,
        product_price: selectedProduct.product_price,
        quantity: quantityInc,
        product_title: selectedProduct.product_title,
        variant_name: selectedProduct.variant_name,
        product_sku: selectedProduct.sku,
        original_product_price: selectedProduct.original_product_price,
        variant_weight: selectedProduct?.variant_weight || "",
        variant_weight_unit: selectedProduct?.variant_weight_unit || "",
        variant_image_url: selectedProduct.image_url,

        exchange_product_id: selectedProduct.product_id,
        exchange_variant_id: exchangeVariant.variant_id,
        exchange_product_sku: exchangeVariant.sku,
        exchange_product_price: exchangeProductPrice,
        exchange_product_title: exchangeProductTitle,
        exchange_variant_image_url: exchangeVariantImage,
        exchange_variant_name: exchangeVariantName,
        original_exchange_product_price: exchangeOriginalPrice,
        exchange_variant_weight: exchangeVariant?.variant_weight || "",
        exchange_variant_weight_unit:
          exchangeVariant?.variant_weight_unit || "",

        return_type: requestReturnType,
        ...(reason?.reason_mode === "reason_first" ? {
          reason_mode: "reason_first",
          reason_resolution_config: resolutionConfig,
        } : {}),
        refund_mode_offer_amount: 0, // Not sure why but we give based on line items as well

        customer_reason_code: reason.reason_code,
        questionnaire_answers: reason.questionnaire_answers || [],
        customer_reason_label: reason.reason_label,
        customer_reason_comment: otherReason,
        customer_sub_reason_code:
          subReason && subReason !== null ? subReason.reason_code : null,
        customer_sub_reason_label:
          subReason && subReason !== null ? subReason.reason_label : null,

        total_item_discount: selectedProduct.total_item_discount,
        discounted_price: selectedProduct.discounted_price,

        exchange_applicable: selectedProduct.exchange_applicable,
        refund_applicable: selectedProduct.refund_applicable,
        shipment_applicable: shipmentApplicable,

        product_group_id:
          selectedProduct.product_group_id || customProductGroupId,
        is_parent: selectedProduct.is_parent,
        is_child: selectedProduct.is_child,
        properties: customProperties || selectedProduct.properties,

        // Not sure why, Just hard coded optional fields
        variant_length: 0,
        variant_breadth: 0,
        variant_height: 0,
        exchange_variant_length: 0,
        exchange_variant_breadth: 0,
        exchange_variant_height: 0,
        discount_applied: false,
        item_amount_difference: 0,
        total_item_amount_difference: 0,

        remaining_qty: selectedProduct.quantity - quantityInc,
        exchange_type: "same",
        applicable_refund_modes: null,
        uploaded_media: selectedLineMedia?.uploads || [],
        uploaded_media_ids: (selectedLineMedia?.uploads || []).map(
          (mediaItem) => mediaItem.media,
        ),
        media_previews: (selectedLineMedia?.uploads || []).map(
          (mediaItem) => mediaItem.url,
        ),
        ...getLineItemMetadata(selectedProduct),
      },
      ...childProducts.map((childProduct) => ({
        merchant_id: merchantConfig.merchant_id,
        line_item_id: childProduct.line_item_id,

        product_id: childProduct.product_id,
        variant_id: childProduct.variant_id,
        product_price: childProduct.product_price,
        quantity: quantityInc,
        product_title: childProduct.product_title,
        variant_name: childProduct.variant_name,
        product_sku: childProduct.sku,
        original_product_price: childProduct.original_product_price,
        variant_weight: childProduct?.variant_weight || "",
        variant_weight_unit: childProduct?.variant_weight_unit || "",
        variant_image_url: childProduct.image_url,

        exchange_product_id: childProduct.product_id,
        exchange_variant_id: childProduct.variant_id,
        exchange_product_sku: childProduct.sku,
        exchange_product_price: childProduct.product_price,
        exchange_product_title: childProduct.product_title,
        exchange_variant_image_url: childProduct.image_url,
        exchange_variant_name: childProduct.variant_name,
        original_exchange_product_price: childProduct.original_product_price,
        exchange_variant_weight: childProduct?.variant_weight || "",
        exchange_variant_weight_unit: childProduct?.variant_weight_unit || "",

        return_type: requestReturnType,
        ...(reason?.reason_mode === "reason_first" ? {
          reason_mode: "reason_first",
          reason_resolution_config: resolutionConfig,
        } : {}),
        refund_mode_offer_amount: 0, // Not sure why but we give based on line items as well

        customer_reason_code: reason.reason_code,
        questionnaire_answers: reason.questionnaire_answers || [],
        customer_reason_label: reason.reason_label,
        customer_reason_comment: otherReason,
        customer_sub_reason_code:
          subReason && subReason !== null ? subReason.reason_code : null,
        customer_sub_reason_label:
          subReason && subReason !== null ? subReason.reason_label : null,

        total_item_discount: childProduct.total_item_discount,
        discounted_price: childProduct.discounted_price,

        exchange_applicable: childProduct.exchange_applicable,
        refund_applicable: childProduct.refund_applicable,
        shipment_applicable: resolutionConfig.shipment_applicable ?? (isReplacement
          ? rmsConfig.shipment_applicable_for_replacements !== false
          : childProduct.shipment_applicable),

        product_group_id: childProduct.product_group_id,
        is_parent: childProduct.is_parent,
        is_child: childProduct.is_child,
        properties: childProduct.properties,

        // Not sure why, Just hard coded optional fields
        variant_length: 0,
        variant_breadth: 0,
        variant_height: 0,
        exchange_variant_length: 0,
        exchange_variant_breadth: 0,
        exchange_variant_height: 0,
        discount_applied: false,
        item_amount_difference: 0,
        total_item_amount_difference: 0,

        remaining_qty: childProduct.quantity - quantityInc,
        exchange_type: "same",
        applicable_refund_modes: null,
        ...getLineItemMetadata(childProduct),
      })),
    ];

    dispatch(addReturnedProducts(nextReturnedProducts));

    dispatch(setReplacementMode(false));
    dispatch(addProceedBool(false));
    setAnimate(true);
    // Reset animation after it's done if needed
    setTimeout(() => {
      setAnimate(false);
    }, 700); // Duration matches the animation

    if (onExchangeComplete) {
      onExchangeComplete(nextReturnedProducts);
    }
  };

  const isVariantUnavailableForExchange = (variant) =>
    Boolean(
      variant &&
        !isOriginalVariant(variant) &&
        (variant.out_of_stock ||
          !variant.quantity_available_for_exchange ||
          (merchantConfig.merchant_id === 541 &&
            variant.inventory_quantity < 7)),
    );

  const findSelectedVariantWithMapping = (variants, selectedAttributes) => {
    return variants.find((variant) => {
      return Object.entries(selectedAttributes).every(
        ([productAttribute, value]) => {
          return variant.attributes.some(
            (variantAttribute) =>
              variantAttribute.product_attribute ===
                parseInt(productAttribute, 10) &&
              variantAttribute.value === value,
          );
        },
      );
    });
  };

  useEffect(() => {
    if (
      effectiveSelectedVariant &&
      effectiveSelectedVariant?.errors &&
      effectiveSelectedVariant.errors.length > 0
    ) {
      document?.querySelector(".out-of-stock")?.scrollIntoView();
    }
  }, [effectiveSelectedVariant]);

  const handleSelectCategory = (categoryId, value) => {
    if (selectedCategories[categoryId] === value) {
      return;
    }

    // Update selected categories
    const updatedSelectedCategories = {
      ...selectedCategories,
      [categoryId]: value,
    };

    // Sort the selected categories by the category order (position) in `categories`
    const sortedSelectedCategories = Object.keys(updatedSelectedCategories)
      .sort((a, b) => {
        const positionA = sameVariantProducts.attributes.find(
          (cat) => cat.id === a,
        )?.position;
        const positionB = sameVariantProducts.attributes.find(
          (cat) => cat.id === b,
        )?.position;
        return positionA - positionB;
      })
      .reduce((acc, key) => {
        acc[key] = updatedSelectedCategories[key];
        return acc;
      }, {});

    const matchedVariant = findSelectedVariantWithMapping(
      sameVariantProducts.variants,
      sortedSelectedCategories,
    );

    // Set the sorted and normalized categories
    setSelectedCategories(sortedSelectedCategories);

    if (
      matchedVariant &&
      (!effectiveSelectedVariant ||
        matchedVariant.variant_id !== effectiveSelectedVariant.variant_id)
    ) {
      setLocalSelectedVariant(matchedVariant);
      openSameVariantConfirmationIfNeeded(matchedVariant);
    }
  };

  const handleExchangeSelectionClick = () => {
    if (isSameVariantSelected) {
      setSameVariantConfirmationOpen(true);
      return;
    }

    handleProductExchange();
  };

  const renderSameVariantConfirmationModal = () => (
    <Modal
      aria-labelledby="same-variant-confirmation-title"
      aria-describedby="same-variant-confirmation-description"
      open={sameVariantConfirmationOpen}
      onClose={() => setSameVariantConfirmationOpen(false)}
      closeAfterTransition
    >
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          minWidth: "300px",
          padding: "20px 24px 24px",
          background: "var(--rms-v2-surface, #fff)",
          maxWidth: "460px",
          width: "90%",
          borderRadius: merchantConfig.use_rounded_edge_buttons
            ? "10px"
            : "0px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "16px",
          }}
        >
          <div>
            <p
              id="same-variant-confirmation-title"
              style={{
                fontWeight: "600",
                fontSize: "16px",
                marginBottom: "8px",
              }}
            >
              Same size selected
            </p>
            <p
              id="same-variant-confirmation-description"
              style={{
                color: "var(--rms-v2-text-body, #3B444D)",
                fontSize: "14px",
              }}
            >
              You selected the same size {selectedProduct?.variant_name || ""}{" "}
              that you originally purchased. Do you want to continue with this
              exchange?
            </p>
          </div>
          <CloseIcon
            style={{
              width: "20px",
              height: "20px",
              stroke: "var(--rms-v2-text-body, #3B444D)",
              cursor: "pointer",
              flexShrink: 0,
            }}
            onClick={() => setSameVariantConfirmationOpen(false)}
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            marginTop: "24px",
          }}
        >
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setSameVariantConfirmationOpen(false)}
          >
            No
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setSameVariantConfirmationOpen(false);
              handleProductExchange();
            }}
          >
            Yes, continue
          </Button>
        </div>
      </div>
    </Modal>
  );

  const CategoryDisplay = ({ category }) => {
    return (
      <div style={{ margin: "10px 0" }}>
        <p
          style={{
            color: "var(--rms-v2-text-body)",
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.8px",
            margin: "0 0 6px",
            textTransform: "uppercase",
          }}
        >
          {category.display_name}
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {category?.values?.map((attr) => {
            const selected = selectedCategories[category.id] === attr.id;
            const candidateSelection = {
              ...selectedCategories,
              [category.id]: attr.id,
            };
            const candidateVariant = findSelectedVariantWithMapping(
              sameVariantProducts.variants,
              candidateSelection,
            );
            const isUnavailable = isVariantUnavailableForExchange(
              candidateVariant,
            );
            const showUnavailableSelectedState = selected && isUnavailable;
            return (
              <div key={attr.id} style={{ position: "relative" }}>
                <input
                  type="radio"
                  id={`${category.id}-${attr.id}`}
                  name={category.name}
                  value={attr.value}
                  checked={selected}
                  onChange={() => handleSelectCategory(category.id, attr.id)}
                  style={{ display: "none" }}
                />
                <p
                  onClick={() => handleSelectCategory(category.id, attr.id)}
                  className={isUnavailable ? "variant-unavailable" : ""}
                  style={{
                    border: isUnavailable
                      ? "1px solid var(--rms-v2-border)"
                      : showUnavailableSelectedState
                      ? "1px solid var(--rms-v2-border)"
                      : selected
                      ? `1px solid ${merchantConfig.primary_theme_color}`
                      : "1px solid var(--rms-v2-border)",
                    backgroundColor: isUnavailable
                      ? "var(--rms-v2-border)"
                      : showUnavailableSelectedState
                      ? "#dce4ef"
                      : selected
                      ? "var(--rms-v2-primary-soft)"
                      : "transparent",
                    color: isUnavailable
                      ? "var(--rms-v2-text-title)"
                      : showUnavailableSelectedState
                      ? "var(--rms-v2-text-title)"
                      : selected
                      ? merchantConfig.primary_theme_color
                      : "var(--rms-v2-text-muted)",
                    borderRadius: merchantConfig.use_rounded_edge_buttons
                      ? "10px"
                      : "0px",
                    padding: "0 16px",
                    margin: 0,
                    minWidth: "50px",
                    minHeight: "30px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    fontWeight: 800,
                    cursor: "pointer",
                    position: "relative",
                    boxShadow: showUnavailableSelectedState ? "none" : "none",
                  }}
                >
                  {attr.display_value}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const activeVariant =
    effectiveSelectedVariant ||
    (!isReplacement &&
    sameVariantProducts?.variants &&
    sameVariantProducts.variants.length > 0
      ? sameVariantProducts.variants[0]
      : null);
  const canContinue =
    !customSizeError &&
    activeVariant &&
    (isReplacement
      ? !isVariantUnavailableForReplacement(activeVariant)
      : isSameVariantSelected ||
      (!activeVariant.out_of_stock &&
        activeVariant.quantity_available_for_exchange &&
        !(
          merchantConfig.merchant_id === 541 &&
          activeVariant.inventory_quantity < 7
        )));

  useEffect(() => {
    if (!isReplacement) {
      hasAutoProcessedRef.current = false;
      return;
    }

    if (
      !sameVariantLoader &&
      effectiveSelectedVariant &&
      canContinue &&
      !hasAutoProcessedRef.current
    ) {
      hasAutoProcessedRef.current = true;
      handleProductExchange();
    }
  }, [
    canContinue,
    effectiveSelectedVariant,
    handleProductExchange,
    isReplacement,
    sameVariantLoader,
  ]);

  useEffect(() => {
    if (!registerSameSubmit) {
      return;
    }

    registerSameSubmit(async () => {
      if (!canContinue) {
        return false;
      }
      if (isSameVariantSelected) {
        setSameVariantConfirmationOpen(true);
        return false;
      }
      handleProductExchange();
      return true;
    });
  }, [
    activeVariant,
    canContinue,
    handleProductExchange,
    isSameVariantSelected,
    registerSameSubmit,
  ]);

  if (isReplacement) {
    return (
      <div>
        {sameApiError ? (
          <LoadingError marginTop="100px" padding="0px 20px" />
        ) : sameVariantLoader ? (
          <p
            style={{
              padding: "12px 0",
              color: "var(--rms-v2-text-muted)",
              fontSize: "14px",
            }}
          >
            Checking replacement availability...
          </p>
        ) : activeVariant && canContinue ? (
          <p
            style={{
              padding: "12px 0",
              color: "var(--rms-v2-text-muted)",
              fontSize: "14px",
            }}
          >
            Adding replacement...
          </p>
        ) : (
          <div className="exchange-feedback exchange-feedback--error out-of-stock">
            <InfoOutlinedIcon className="exchange-feedback-icon" />
            <div className="exchange-feedback-content">
              {activeVariant?.errors && activeVariant.errors.length > 0 ? (
                <ul className="exchange-feedback-list">
                  {activeVariant.errors.map((error) => (
                    <li key={error.error_code}>{error.error_code_message}</li>
                  ))}
                </ul>
              ) : (
                <p className="exchange-feedback-text">
                  Replacement product is not available in the requested quantity.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        {sameApiError ? (
          <LoadingError marginTop="100px" padding="0px 20px" />
        ) : sameVariantLoader ? (
          <p
            style={{
              padding: "12px 0",
              color: "var(--rms-v2-text-muted)",
              fontSize: "14px",
            }}
          >
            Loading variants...
          </p>
        ) : sameVariantProducts.variants.length > 0 &&
          activeVariant !== null ? (
          <div
            style={{
              margin: "10px 0px",
              width: "100%",
            }}
          >
            <StyledDifferentProduct
              rounded={merchantConfig.use_rounded_edge_buttons}
              className="same-product-summary"
            >
              <div style={{ display: "flex", width: "100%" }}>
                <div
                  className="image-container"
                  style={{
                    width: "64px",
                    minWidth: "64px",
                    height: "64px",
                    borderRadius: merchantConfig.use_rounded_edge_buttons
                      ? "10px"
                      : "0px",
                    overflow: "hidden",
                    background: "var(--rms-v2-surface-muted)",
                    marginRight: "10px",
                  }}
                >
                  <img
                    src={
                      activeVariant?.variant_img?.src ||
                      activeVariant?.variant_img ||
                      activeVariant?.src
                    }
                    alt={
                      activeVariant?.variant_img?.alt ||
                      sameVariantProducts.title
                    }
                    className="product-image"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
                <div>
                  {sameVariantProducts.title &&
                    sameVariantProducts.title !== "" && (
                      <p className="product-title">
                        {sameVariantProducts.title}
                      </p>
                    )}
                  <p
                    className="same-product-variant"
                    style={{
                      color: "var(--rms-v2-text-muted)",
                    }}
                  >
                    {activeVariant.title.indexOf("Default") === -1
                      ? activeVariant.title
                      : sameVariantProducts.title}
                  </p>
                  <div
                    className="product-details"
                    style={{ paddingLeft: "0px", marginLeft: "0px" }}
                  >
                    {merchantConfig.merchant_id === 977 && (
                      <div>
                        <p
                          style={{
                            color: "var(--rms-v2-text-body)",
                            fontSize: "16px",
                            margin: "5px 0px",
                            fontWeight: "700",
                          }}
                        >
                          {rmsConfig.discount_config &&
                            Object.keys(rmsConfig.discount_config).length > 0 &&
                            rmsConfig.discount_config.target_type ===
                              "line_item" && (
                              <span>
                                ₹{" "}
                                {rmsConfig.discount_config.type === "fixed"
                                  ? activeVariant.price -
                                    rmsConfig.discount_config.value
                                  : (
                                      activeVariant.price -
                                      (activeVariant.price *
                                        rmsConfig.discount_config.value) /
                                        100
                                    ).toFixed(2)}
                              </span>
                            )}
                          <span
                            style={{
                              textDecoration:
                                rmsConfig.discount_config &&
                                Object.keys(rmsConfig.discount_config).length >
                                  0
                                  ? "line-through"
                                  : "none",
                              marginLeft:
                                rmsConfig.discount_config &&
                                Object.keys(rmsConfig.discount_config).length >
                                  0
                                  ? "10px"
                                  : "0px",
                              color:
                                rmsConfig.discount_config &&
                                Object.keys(rmsConfig.discount_config).length >
                                  0
                                  ? "var(--rms-v2-text-body)66"
                                  : "var(--rms-v2-text-body)",
                            }}
                          >
                            ₹ {activeVariant.price}
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="same-variant-selector-section">
                {merchantConfig.merchant_id === 977 &&
                  sameVariantProducts.attributes.length > 0 &&
                  sameVariantProducts.attributes
                    .filter((category) => category.name !== "size")
                    .map((category) => {
                      return (
                        <CategoryDisplay
                          key={category.id}
                          category={category}
                        />
                      );
                    })}
                {merchantConfig.merchant_id === 977 &&
                  sameVariantProducts.attributes.length > 0 &&
                  sameVariantProducts.attributes
                    .filter((category) => category.name === "size")
                    .map((category) => {
                      const groupByType = (items) => {
                        return items.reduce((acc, item) => {
                          const types = ["single", "double", "queen", "king"];
                          const type = types.find((t) =>
                            item.value.includes(t),
                          );
                          if (!type) return acc;

                          const cleanedDisplayValue = item.display_value
                            .replace(/\s*\(\w+\)\s*$/, "")
                            .trim();

                          const formattedItem = {
                            ...item,
                            group: type,
                            display_value: cleanedDisplayValue,
                          };

                          acc[type] = acc[type] || [];
                          acc[type].push(formattedItem);
                          return acc;
                        }, {});
                      };

                      const grouped = groupByType(category.values);

                      if (Object.keys(grouped).length > 0) {
                        return (
                          <SizeSelector
                            key={category.id}
                            grouped={grouped}
                            sizeAttributeId={category.id}
                            onCustomSizeError={setCustomSizeError}
                            variants={sameVariantProducts.variants}
                            setSelectedVariant={setLocalSelectedVariant}
                            onVariantSelected={
                              openSameVariantConfirmationIfNeeded
                            }
                            selectedVariant={effectiveSelectedVariant}
                            setCustomProperties={setCustomProperties}
                            selectedProduct={selectedProduct}
                            setCustomProductGroupId={setCustomProductGroupId}
                          />
                        );
                      } else {
                        return (
                          <CategoryDisplay
                            key={category.id}
                            category={category}
                          />
                        );
                      }
                    })}
                {merchantConfig.merchant_id !== 977 &&
                  sameVariantProducts?.attributes.length > 0 &&
                  sameVariantProducts?.attributes.map((category) => {
                    return (
                      <CategoryDisplay key={category.id} category={category} />
                    );
                  })}
              </div>
              {effectiveSelectedVariant &&
                effectiveSelectedVariant !== null &&
                (isReplacement
                  ? isVariantUnavailableForReplacement(effectiveSelectedVariant)
                  : !isSameVariantSelected &&
                    isVariantUnavailableForExchange(effectiveSelectedVariant)) && (
                  <div className="exchange-feedback exchange-feedback--error out-of-stock">
                    <InfoOutlinedIcon className="exchange-feedback-icon" />
                    <div className="exchange-feedback-content">
                      {effectiveSelectedVariant?.errors &&
                      effectiveSelectedVariant.errors.length > 0 ? (
                        <ul className="exchange-feedback-list">
                          {effectiveSelectedVariant.errors.map((error) => (
                            <li key={error.error_code}>
                              {error.error_code_message}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="exchange-feedback-text">Out of stock</p>
                      )}
                    </div>
                  </div>
                )}
              {effectiveSelectedVariant &&
                selectedProduct &&
                isSameVariantSelected && (
                  <div className="exchange-feedback exchange-feedback--info same-variant-alert">
                    <InfoOutlinedIcon className="exchange-feedback-icon" />
                    <div className="exchange-feedback-content">
                      <p className="exchange-feedback-text">
                        You have selected the same variant{" "}
                        {selectedProduct.variant_name} that you originally
                        purchased.
                      </p>
                    </div>
                  </div>
                )}
            </StyledDifferentProduct>
            {/* <div
              style={{
                padding: "0 1rem",
                fontSize: "14px",
                marginTop: "10px",
                color: "var(--rms-v2-text-body)",
              }}
            >
              {parse(sameVariantProducts?.rich_description || "")}
            </div> */}
            {showInternalContinue && (
              <div
                style={{
                  position: "sticky",
                  bottom: 0,
                  background: "var(--rms-v2-surface)",
                  padding: "10px 14px 14px",
                  borderTop: "1px solid var(--rms-v2-border-soft)",
                }}
              >
                <Button
                  color="primary"
                  fullWidth
                  onClick={handleExchangeSelectionClick}
                  variant="contained"
                  style={{
                    height: "40px",
                    minHeight: "40px",
                    padding: "0 14px",
                    borderRadius: merchantConfig.use_rounded_edge_buttons
                      ? "10px"
                      : "0px",
                  }}
                  disabled={!canContinue}
                >
                  {actionLabel}
                </Button>
              </div>
            )}
            {renderSameVariantConfirmationModal()}
          </div>
        ) : (
          <div
            style={{
              justifyContent: "center",
              textAlign: "center",
              marginTop: "4rem",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Search />
            <p style={{ textAlign: "center", marginTop: "1.5rem" }}>
              Hmm we don't have that..yet
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default SameProduct;
